import React from 'react'
import {
  Dialog,
  DialogActions,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Paper,
} from '@mui/material'
import { InvoiceCheckDetailSummary } from 'src/viewModels/InvoiceCheckDetailSummary'
import { formatCurrency, formatDate } from 'src/utils/Formatter'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import { ArchiveOutline } from 'mdi-material-ui'
import { CreditCard } from '@mui/icons-material'

interface Props {
  open: boolean
  onClose: () => void
  check: InvoiceCheckDetailSummary | null
  isUnapplied?: boolean
}

const CheckHistoryModal: React.FC<Props> = ({ open, onClose, check, isUnapplied }) => {
  if (!check) return null

  const modalTitle = isUnapplied
    ? `Unapplied Details - Check Number: ${check.checkNumber}, Check Date: ${
        check.checkDate ? formatDate(check.checkDate.toString()) : ''
      }, Check Amount: ${formatCurrency(check.checkTotal?.toString() ?? '0')}`
    : `Applied Details - Check Number: ${check.checkNumber}, Check Date: ${
        check.checkDate ? formatDate(check.checkDate.toString()) : ''
      }, Check Amount: ${formatCurrency(check.checkTotal?.toString() ?? '0')}`
  const modalIcon = isUnapplied ? <ArchiveOutline /> : <CreditCard />

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <ResponsiveAppBar title={modalTitle} pageIcon={modalIcon} isModal />
      <div style={{ margin: '20px 20px 0 20px', fontSize: 18 }}>
        <b>Payer</b>:&nbsp;&nbsp;
        {check.payer === undefined || check.payer === '' ? '[UNKNOWN]' : check.payer}
      </div>
      <Paper style={{ marginTop: '20px', marginRight: '20px', marginLeft: '20px' }}>
        <Table aria-label="check details" style={{ padding: '20px' }}>
          <TableHead>
            <TableRow>
              <TableCell>Applied On</TableCell>
              <TableCell>Applied By</TableCell>
              <TableCell>Case Number</TableCell>
              <TableCell>Patient Name</TableCell>
              <TableCell>Referral Source</TableCell>
              <TableCell>Applied Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {check.invoicePaymentCheckAppliedFundsDetail &&
            check.invoicePaymentCheckAppliedFundsDetail.length > 0 ? (
              check.invoicePaymentCheckAppliedFundsDetail.map((fund, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    {fund.appliedOn ? formatDate(fund.appliedOn.toString()) : ''}
                  </TableCell>
                  <TableCell>{fund.appliedBy}</TableCell>
                  <TableCell>{fund.caseNumber}</TableCell>
                  <TableCell>{fund.patientName}</TableCell>
                  <TableCell>{fund.referralSource}</TableCell>
                  <TableCell>
                    {formatCurrency(fund.appliedAmount?.toString() ?? '0')}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No applied funds history found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          <tfoot>
            <TableRow>
              <TableCell colSpan={5} align="right">
                <strong>Total Applied Amount:</strong>
              </TableCell>
              <TableCell>
                <strong>
                  {formatCurrency(
                    (
                      check.invoicePaymentCheckAppliedFundsDetail?.reduce(
                        (sum, fund) => sum + (Number(fund.appliedAmount) || 0),
                        0
                      ) ?? 0
                    ).toString()
                  )}
                </strong>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={5} align="right">
                <strong>Remaining Funds:</strong>
              </TableCell>
              <TableCell>
                <strong>
                  {formatCurrency(
                    (
                      (Number(check.checkTotal) || 0) -
                      (check.invoicePaymentCheckAppliedFundsDetail?.reduce(
                        (sum, fund) => sum + (Number(fund.appliedAmount) || 0),
                        0
                      ) ?? 0)
                    ).toString()
                  )}
                </strong>
              </TableCell>
            </TableRow>
          </tfoot>
        </Table>
      </Paper>
      <DialogActions>
        <Grid container justifyContent="flex-end" spacing={1}>
          <Grid item>
            <Button onClick={onClose} color="primary">
              Close
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  )
}

export default CheckHistoryModal
